import {
  makeInfiniteQueryOptions,
  makeQueryOptions,
} from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { lightFormat, sub } from 'date-fns';
import { z } from 'zod';

import {
  AvailableFiltersSchema,
  BulkPublishRequestSchema,
  ForecastedKPIItemSchema,
  GenerateRequestSchema,
  GetFilterRequestSchema,
  GetFilterResponseSchema,
  ListRequestSchema,
  ListResponseSchema,
  MediarowPermissionResponseSchema,
  PlatformPermissionResponseSchema,
  StatusSchema,
  SuggestionPlatformsResponseSchema,
  SuggestionResponseSchema,
  TabsSchema,
  TaxonomyChildrenSchema,
  TaxonomySchema,
  type SuggestionPlatformSchema,
} from './schemas.js';

export const rollback = defaultApi.declareEndpoint({
  url: 'recommendations/$id/rollback' as const,
  method: 'POST',
  responseSchema: SuggestionResponseSchema,
});

export const generate = defaultApi.declareEndpoint({
  url: 'recommendations/$id/trigger/generate' as const,
  method: 'POST',
  responseSchema: z.object({
    status: z.string(),
  }),
  requestSchema: GenerateRequestSchema,
});

export const list = defaultApi.declareEndpoint({
  url: 'recommendations/list' as const,
  method: 'GET',
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  responseSchema: ListResponseSchema,
  querySchema: ListRequestSchema,
});

export const getSuggestion = defaultClient.query({
  url: 'recommendations/$id' as const,
  method: 'GET',
  responseSchema: SuggestionResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const listInfiniteQueryOptions = makeInfiniteQueryOptions(
  list,
  {
    processResponse(data) {
      return data;
    },
    getNextPageParam: () => ({
      dateFrom: lightFormat(sub(new Date(), { days: 90 }), 'yyyy-MM-dd'),
      dateTo: lightFormat(new Date(), 'yyyy-MM-dd'),
      sortBy: 'date',
      campaigns: [],
      platforms: [],
      channels: [],
      statuses: 'ACTIVE,REWORKING',
      page: 1,
      pageSize: 10,
      campaignName: '',
    }),
  },
  {
    getNextPageParam: (lastPage: ListResponseType) =>
      lastPage.pages > lastPage.page ? lastPage.page + 1 : undefined,
    staleTime: 900000, // 15 minutes
    refetchInterval: 1000 * 60 * 5, //  5 minutes to auto refetching is enough.
    refetchOnWindowFocus: false,
  },
);

export const getFilters = defaultApi.declareEndpoint({
  url: 'recommendations/list/filters' as const,
  method: 'GET',
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  responseSchema: GetFilterResponseSchema,
  querySchema: GetFilterRequestSchema,
});

export const getFiltersQueryOptions = makeQueryOptions(
  getFilters,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    refetchInterval: false,
    refetchOnWindowFocus: false,
  },
);

export const reject = defaultApi.declareEndpoint({
  url: 'recommendations/$id/reject' as const,
  method: 'POST',
  responseSchema: SuggestionResponseSchema,
  requestSchema: z.object({
    reason: z.string(),
  }),
});

export const accept = defaultApi.declareEndpoint({
  url: 'recommendations/$suggestionId/accept' as const,
  method: 'POST',
  responseSchema: SuggestionResponseSchema,
  requestSchema: z.object({
    suggestionId: z.number().or(z.string()),
    mediaPlanId: z.number().optional(),
  }),
});

export const bulkPublish = defaultClient.mutation({
  url: 'recommendations/publish' as const,
  method: 'POST',
  requestSchema: BulkPublishRequestSchema,
  responseSchema: ListResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const read = defaultApi.declareEndpoint({
  url: 'recommendations/$id/set_recommendation_read' as const,
  method: 'POST',
  responseSchema: SuggestionResponseSchema,
});

export const suggestionPermissions = defaultApi.declareEndpoint({
  url: 'recommendations/$id/platform_permissions' as const,
  method: 'GET',
  responseSchema: PlatformPermissionResponseSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getSuggestionPermissions = makeQueryOptions(
  suggestionPermissions,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    refetchInterval: false,
    refetchOnWindowFocus: false,
  },
);

export const suggestionPlatforms = defaultApi.declareEndpoint({
  url: 'recommendations/$id/platform_types' as const,
  method: 'GET',
  responseSchema: SuggestionPlatformsResponseSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getSuggestionPlatforms = makeQueryOptions(
  suggestionPlatforms,
  {
    processResponse(data) {
      return data;
    },
  },
  {
    refetchInterval: false,
    refetchOnWindowFocus: false,
  },
);

export type ListResponseType = z.infer<typeof ListResponseSchema>;
export type GetFilterRequestType = z.infer<typeof GetFilterRequestSchema>;
export type GetFilterResponseType = z.infer<typeof GetFilterResponseSchema>;
export type ListRequestType = z.infer<typeof ListRequestSchema>;
export type ForecastedKPIItemType = z.infer<typeof ForecastedKPIItemSchema>;
export type TabsType = z.infer<typeof TabsSchema>;
export type TaxonomySchemaType = z.infer<typeof TaxonomySchema>;
export type TaxonomyChildrenType = z.infer<typeof TaxonomyChildrenSchema>;
export type GenerateRequestType = z.infer<typeof GenerateRequestSchema>;
export type SuggestionResponseType = z.infer<typeof SuggestionResponseSchema>;
export type BulkPublishRequestType = z.infer<typeof BulkPublishRequestSchema>;
export type StatusesType = z.infer<typeof StatusSchema>;
export type AvailableFiltersType = z.infer<typeof AvailableFiltersSchema>;
export type SuggestionPlatform = z.infer<typeof SuggestionPlatformSchema>;
export type SuggestionPlatformResponse = z.infer<
  typeof SuggestionPlatformsResponseSchema
>;

export type MediarowPermissionResponseType = z.infer<
  typeof MediarowPermissionResponseSchema
>;
export type PlatformPermissionResponseType = z.infer<
  typeof PlatformPermissionResponseSchema
>;

export const Statuses = StatusSchema.def.entries;
